import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useI18n } from "../i18n";

const IMAGES = [
    "/images/use-cases/case-1.png",
    "/images/use-cases/case-2.png",
    "/images/use-cases/case-3.png",
    "/images/use-cases/case-4.png",
];

export default function UseCases() {
    const { dict, t } = useI18n();
    const items = dict.cases.items;
    return (
        <section id="cases" data-testid="cases-section" className="section-pad relative scroll-mt-24">
            <div className="container-page">
                <div className="mb-16 max-w-3xl">
                    <span className="eyebrow">{t("cases.eyebrow")}</span>
                    <h2
                        data-testid="cases-title"
                        className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter whitespace-pre-line"
                    >
                        {t("cases.title")}
                    </h2>
                </div>

                <div className="space-y-24 md:space-y-32">
                    {items.map((it, idx) => {
                        const reverse = idx % 2 === 1;
                        return (
                            <motion.div
                                key={it.title}
                                data-testid={`use-case-${idx}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.6 }}
                                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${
                                    reverse ? "lg:[direction:rtl]" : ""
                                }`}
                            >
                                <div className={`lg:col-span-6 lg:[direction:ltr]`}>
                                    <div className="relative aspect-[5/4] lg:aspect-[6/5] overflow-hidden rounded-2xl border border-white/10">
                                        <img
                                            src={IMAGES[idx % IMAGES.length]}
                                            alt={it.title}
                                            loading="lazy"
                                            className="absolute inset-0 h-full w-full object-cover scale-105 transition-transform duration-[1.4s] hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/30 to-transparent" />
                                        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs text-white/70">
                                            <span className="inline-flex items-center gap-2">
                                                <span className="h-2 w-2 rounded-full bg-brand-glow animate-pulse-glow" />
                                                Case 0{idx + 1}
                                            </span>
                                            <span className="font-mono opacity-70">/ {String(idx + 1).padStart(2, "0")}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="lg:col-span-6 lg:[direction:ltr]">
                                    <span className="text-xs font-mono text-brand-glow tracking-widest uppercase">
                                        Case {String(idx + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="mt-3 text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
                                        {it.title}
                                    </h3>
                                    <p className="mt-4 text-white/60 text-base md:text-lg leading-relaxed">
                                        {it.desc}
                                    </p>
                                    <ul className="mt-6 space-y-2.5">
                                        {it.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-3 text-sm text-white/75">
                                                <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand/15 border border-brand/40 text-brand-glow">
                                                    <Check className="h-3 w-3" />
                                                </span>
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
